import { useQuery } from "@tanstack/react-query";
import { CalendarRange } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VariationBadge } from "@/components/PriceTag";
import { cn } from "@/lib/utils";

interface Props {
  forumId: number | string;
  currency?: string | null;
}

const FR_NUMBER = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 });
const FR_DATE = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

function fmt(v: number | null | undefined) {
  return v == null ? "—" : FR_NUMBER.format(v);
}

export function ForumHistory({ forumId, currency }: Props) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["forum-history", forumId],
    queryFn: () => api.forumHistory(forumId),
    enabled: forumId != null,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarRange className="size-4" />
          Historique ({data.length} sessions)
          {currency && (
            <span className="text-xs font-normal text-foreground-muted">en {currency}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : data.length === 0 ? (
          <p className="text-sm text-foreground-muted">
            Aucun historique scrapé. Cliquez « Rafraîchir » pour récupérer le dernier mois.
          </p>
        ) : (
          <div className="max-h-[420px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Dernier</TableHead>
                  <TableHead className="text-right">Var.</TableHead>
                  <TableHead className="text-right">Ouv.</TableHead>
                  <TableHead className="text-right">+ Haut</TableHead>
                  <TableHead className="text-right">+ Bas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((r) => (
                  <TableRow key={r.session_date}>
                    <TableCell className="text-sm tabular-nums">
                      {FR_DATE.format(new Date(r.session_date))}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums font-medium",
                        r.variation_pct != null && r.variation_pct > 0 && "text-emerald-500",
                        r.variation_pct != null && r.variation_pct < 0 && "text-red-500"
                      )}
                    >
                      {fmt(r.last_price)}
                    </TableCell>
                    <TableCell className="text-right">
                      <VariationBadge value={r.variation_pct} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-foreground-muted">
                      {fmt(r.open_price)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600/80">
                      {fmt(r.high_price)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600/80">
                      {fmt(r.low_price)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
